import React, { useState, useMemo, useRef, useId } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  ColumnFiltersState,
} from '@tanstack/react-table';

import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  TabsAndSearchRow,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  TableResultCount,
  FilterBarRow,
  TableTabList,
  HeaderSortingIndicator,
  SearchResultsBanner,
} from '../Table';
import { TableSearchWithPreview } from '../TableSearchWithPreview';
import { ExpiringPriorAuthPreviewItem } from './ExpiringPriorAuthPreviewItem';
import { filterExpiringPriorAuth } from './priorAuthSearchUtils';
import FormattedCell from 'design-system/src/components/FormattedCell/FormattedCell';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ExpiringPriorAuthTableRow } from '../../api';
import { TableMultiselectFilter } from 'design-system/src/components/TableDropdownFilter/TableDropdownFilter';
import {
  type MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import {
  sortOfficeNames,
  getInitialTableColumnFilterValueFromCookie,
  getDropdownInitialValueFromSearchParams,
  getDropdownInitialValueFromSearchParamsAndCookie,
  updateTableColumnFilterValueCookie,
  expiringPriorAuthFilterValuesFromSearchParams,
  updateUrlSearchParams,
  getPrescriberFilterValue,
} from '../../utils';
import { formatStandardDateLocaleString } from 'shared-utils/utils/formatDate';
import { MULTI_SELECT_DELINEATOR } from '../../constants';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';
import styled from 'styled-components';
import { focusVisibleStyles } from 'design-system/src/sharedStyles';
import { AuthContext } from '../../auth';

import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from 'design-system/src/components/SaveFiltersButton/SaveFiltersButton';
import { areStringArraysEqual } from '../../utils';
import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';
import { createTrackingProps } from 'shared-utils/analytics';
import Checkmark from 'design-system/src/assets/icons/checkmarkThin.svg?react';

const expiringPriorAuthColumnHelper = createColumnHelper<ExpiringPriorAuthTableRow>();

type ExpiringPriorAuthTabValue = 'open' | 'dismissed';

interface ExpiringPriorAuthTableProps {
  data: ExpiringPriorAuthTableRow[];
  onDismiss: (expiringPriorAuthId: string) => void;
  onRestore: (expiringPriorAuthId: string) => void;
}

const OuterTableContainer = styled.div`
  overflow-y: visible;
  height: 100%;
`;

const DismissalButton = styled.a`
  color: var(--grayscale-600);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(0, 0, 0, 0);
  transition: text-decoration-color 0.3s;
  &:hover {
    text-decoration-color: var(--grayscale-600);
  }
  ${focusVisibleStyles}
`;

const DismissedRow = styled(ClickableRow)`
  opacity: 0.5;
  transition: opacity 0.3s;
`;

const expiringPriorAuthTableMultiSelectFilter = (
  row: Row<ExpiringPriorAuthTableRow>,
  columnId: string,
  filterValue: string[],
) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

// prescriber column needs a special filter function because it's in a nested
// object
const prescriberMultiSelectFilter = (row: Row<ExpiringPriorAuthTableRow>, _: string, filterValue: string[]) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  return filterValue.includes(row.original.rx.prescriber_name || '');
};

const getDefaultNullStateType = (selectedTab: ExpiringPriorAuthTabValue) => {
  if (selectedTab === 'dismissed') {
    return TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_DISMISSED;
  } else {
    return TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_OPEN;
  }
};

const DismissCell: React.FC<{
  dismissed: boolean;
  onDismiss: (expiringPriorAuthId: string) => void;
  onRestore: (expiringPriorAuthId: string) => void;
  expiringPriorAuthId: string;
}> = ({ dismissed, onDismiss, onRestore, expiringPriorAuthId }) => {
  if (!dismissed)
    return (
      <DismissalButton
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(expiringPriorAuthId);
        }}
      >
        Dismiss
      </DismissalButton>
    );
  else
    return (
      <DismissalButton
        onClick={(e) => {
          e.stopPropagation();
          onRestore(expiringPriorAuthId);
        }}
      >
        Restore
      </DismissalButton>
    );
};

const ExpiringPriorAuthTable: React.FC<ExpiringPriorAuthTableProps> = ({ data, onDismiss, onRestore }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const initialTab = statusParam === 'open' || statusParam === 'dismissed' ? statusParam : 'open';
  const [selectedTab, setSelectedTab] = useState<ExpiringPriorAuthTabValue>(initialTab);
  const tabListId = useId();
  const panelId = useId();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const { data: filterOptions } = useFetchPortalTableFilters();

  // Add search value state for controlled input
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get('search') || '');

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames } = useDefaultFilters();
  const officeCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'office_name'),
    [authContext.user?.member_id],
  );
  const drugCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'drug_name'),
    [authContext.user?.member_id],
  );
  const hasUserClearedFilters = React.useRef(false);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(() => {
    // Convert defaultPrescriberNames to NPIs for comparison
    const defaultPrescriberNpis =
      defaultPrescriberNames
        ?.map((name) => {
          const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
          return prescriber?.value;
        })
        .filter((npi): npi is string => npi !== undefined) || [];

    return !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis);
  }, [currentPrescriberFilterNpis, defaultPrescriberNames, filterOptions?.prescribers]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Filter data by tab (used when search is not active)
  const tabFilteredData = useMemo(
    () => data.filter((row) => (selectedTab === 'dismissed' ? row.pa.dismissed : !row.pa.dismissed)),
    [data, selectedTab],
  );

  // When search is active, show results from all tabs; otherwise show only current tab
  const tableData = appliedSearch ? data : tabFilteredData;

  const expiringPriorAuthPatientSortingFn = (
    a: Row<ExpiringPriorAuthTableRow>,
    b: Row<ExpiringPriorAuthTableRow>,
    columnId: string,
  ) => {
    const aValue = a.getValue(columnId) as { name: string; dob: string };
    const bValue = b.getValue(columnId) as { name: string; dob: string };
    return bValue.name.localeCompare(aValue.name);
  };

  const expiringPriorAuthDrugSortingFn = (
    a: Row<ExpiringPriorAuthTableRow>,
    b: Row<ExpiringPriorAuthTableRow>,
    columnId: string,
  ) => {
    const aValue = a.getValue(columnId) as { name: string; dateReceived: string; dateExpires: string };
    const bValue = b.getValue(columnId) as { name: string; dateReceived: string; dateExpires: string };
    return bValue.name.localeCompare(aValue.name);
  };

  const columns = [
    expiringPriorAuthColumnHelper.accessor(
      (row) => {
        return { name: row.rx.patient_name, dob: row.formatted_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: expiringPriorAuthPatientSortingFn,
        cell: (info) => {
          const isDismissed = appliedSearch && info.row.original.pa.dismissed;
          return (
            <FormattedCell
              id={info.row.original.rx.id}
              title={info.getValue().name}
              subtitle={`DOB: ${info.getValue().dob}`}
              customBody={
                isDismissed ? (
                  <span className="flex items-center gap-1">
                    <Typography
                      styledAs="bodyExtraSmallDMSans"
                      renderedAs="span"
                      color="var(--grayscale-500)"
                      className="!italic"
                    >
                      Dismissed
                    </Typography>
                    <Checkmark className="size-3 text-grayscale-500" aria-hidden />
                  </span>
                ) : undefined
              }
            />
          );
        },
      },
    ),
    expiringPriorAuthColumnHelper.accessor(
      (row) => {
        return {
          name: row.drug_name,
          dateReceived: row.formatted_prescription_recieved_at,
          dateExpires: row.formatted_approval_until,
        };
      },
      {
        id: 'drug',
        header: 'Drug',
        sortingFn: expiringPriorAuthDrugSortingFn,
        filterFn: (row, _, filterValue) => {
          if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
          // drug_description requires special handling because we're looking for a substring match
          // whereas the other columns are looking for an exact match
          const drugName = row.original.drug_name?.toLowerCase() || '';
          return filterValue.some((filterVal) => drugName.includes(filterVal.toLowerCase()));
        },
        cell: (info) => (
          <FormattedCell
            id={info.row.original.rx.id}
            title={info.getValue().name}
            subtitle={`Prescribed ${formatStandardDateLocaleString(info.getValue().dateReceived)}`}
            customBody={
              info.getValue().dateExpires ? (
                <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div" color="var(--red-400)" weight={600}>
                  Expires {formatStandardDateLocaleString(info.getValue().dateExpires)}
                </Typography>
              ) : undefined
            }
          />
        ),
      },
    ),
    expiringPriorAuthColumnHelper.accessor('rx.prescriber_name', {
      header: 'Prescriber',
      id: 'prescriber_name',
      filterFn: prescriberMultiSelectFilter,
    }),
    expiringPriorAuthColumnHelper.accessor('pa.dismissed', {
      header: '',
      enableGlobalFilter: false,
      enableSorting: false,
      cell: (info) => (
        <DismissCell
          dismissed={info.getValue()}
          onDismiss={onDismiss}
          onRestore={onRestore}
          expiringPriorAuthId={info.row.original.pa.id}
        />
      ),
    }),

    // this column is only used for filtering, not displaying
    expiringPriorAuthColumnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: expiringPriorAuthTableMultiSelectFilter,
    }),
  ];

  const table = useReactTable({
    data: tableData,
    state: {
      columnFilters,
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    enableSorting: true,
    enableGlobalFilter: true,
    // Use doesSearchMatch for consistent fuzzy date and name format matching
    globalFilterFn: (row, _, filterValue) => {
      if (!filterValue || typeof filterValue !== 'string') return true;
      return filterExpiringPriorAuth(row.original, filterValue);
    },
    initialState: {
      columnVisibility: { office_name: false },
      globalFilter: searchValue,
    },
  });

  React.useEffect(() => {
    const searchFromParams = searchParams.get('search') || '';
    setSearchValue(searchFromParams);
    setAppliedSearch(searchFromParams);
    table.setGlobalFilter(searchFromParams);

    const filterValues = expiringPriorAuthFilterValuesFromSearchParams(searchParams);

    const prescriberValue = getPrescriberFilterValue(
      filterValues,
      filterOptions,
      defaultPrescriberNames,
      hasUserClearedFilters,
    );

    setColumnFilters([
      {
        id: 'office_name',
        value: filterValues.office.length
          ? filterValues.office
          : hasUserClearedFilters.current
            ? []
            : officeCookieValues,
      },
      {
        id: 'prescriber_name',
        value: prescriberValue,
      },
      {
        id: 'drug',
        value: filterValues.drug_name.length
          ? filterValues.drug_name
          : hasUserClearedFilters.current
            ? []
            : drugCookieValues,
      },
    ]);
  }, [searchParams, table, userEmail, defaultPrescriberNames, filterOptions, officeCookieValues, drugCookieValues]);

  // Sync selectedTab with URL status changes (e.g., browser back/forward)
  React.useEffect(() => {
    const statusParam = searchParams.get('status');
    const newTab = statusParam === 'open' || statusParam === 'dismissed' ? statusParam : 'open';
    if (newTab !== selectedTab) {
      setSelectedTab(newTab);
    }
  }, [searchParams, selectedTab]);

  const nullStateType = useTableNullState({
    columnFilters,
    filterIds: ['office_name', 'prescriber_name', 'drug'],
    defaultNullStateType: getDefaultNullStateType(selectedTab),
    globalFilter: searchValue,
  });

  const clearSearch = React.useCallback(() => {
    setAppliedSearch('');
    setSearchValue('');
    table.setGlobalFilter('');
    updateUrlSearchParams(searchParams, setSearchParams, {
      search: undefined,
    });
  }, [table, searchParams, setSearchParams]);

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters
    setColumnFilters([]);

    // Clear global filter and search input
    table.setGlobalFilter('');
    setSearchValue('');
    setAppliedSearch('');

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'office_name');
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'drug_name');

    // Clear TableMultiselectFilter refs
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
    drugFilterRef.current?.clear();

    // Mark that user has explicitly cleared filters
    hasUserClearedFilters.current = true;

    updateUrlSearchParams(searchParams, setSearchParams, {
      search: undefined,
      office: undefined,
      prescriber: undefined,
      drug_name: undefined,
    });
  }, [table, searchParams, setSearchParams, authContext.user?.member_id]);

  return (
    <>
      <OuterTableContainer>
        <TableContainer>
          <FilterBar>
            <TabsAndSearchRow>
              <TableTabList
                tabs={[
                  { label: 'Open', value: 'open' },
                  { label: 'Dismissed', value: 'dismissed' },
                ]}
                selectedValue={appliedSearch ? '' : selectedTab}
                onSelect={(value) => {
                  setSelectedTab(value as ExpiringPriorAuthTabValue);
                  // Clear search when switching tabs
                  if (appliedSearch) {
                    setAppliedSearch('');
                    setSearchValue('');
                    table.setGlobalFilter('');
                  }
                  updateUrlSearchParams(searchParams, setSearchParams, {
                    status: value,
                    search: undefined,
                  });
                }}
                tabListId={tabListId}
                tabPanelId={panelId}
                ariaLabel="Filter prior authorizations by status"
                {...createTrackingProps({ elementName: 'Prior Auth Table: Status Tabs', unmask: true })}
              />
              <TableSearchWithPreview
                tableName="Expiring Prior Auth Table"
                value={searchValue}
                onChange={setSearchValue}
                onApplySearch={(value) => {
                  setAppliedSearch(value);
                  table.setGlobalFilter(value);
                  updateUrlSearchParams(searchParams, setSearchParams, {
                    search: value === '' ? undefined : value,
                  });
                }}
                onClear={() => {
                  setAppliedSearch('');
                  setSearchValue('');
                  table.setGlobalFilter('');
                  updateUrlSearchParams(searchParams, setSearchParams, {
                    search: undefined,
                  });
                }}
                data={data}
                filterFn={filterExpiringPriorAuth}
                getItemKey={(expiringPriorAuth) => expiringPriorAuth.pa.id}
                onPreviewItemClick={(expiringPriorAuth) => navigate(`/rxs/${expiringPriorAuth.rx.id}`)}
                renderPreviewItem={(expiringPriorAuth) => (
                  <ExpiringPriorAuthPreviewItem expiringPriorAuth={expiringPriorAuth} searchTerm={searchValue} />
                )}
              />
            </TabsAndSearchRow>

            {appliedSearch ? (
              <SearchResultsBanner
                searchTerm={appliedSearch}
                onClearSearch={clearSearch}
                count={table.getRowModel().rows?.length ?? 0}
              />
            ) : (
              <FilterBarRow>
                <TableMultiselectFilter
                  options={filterOptions?.offices || []}
                  label={'Office'}
                  initialValue={
                    getDropdownInitialValueFromSearchParamsAndCookie(
                      searchParams.get('office') ||
                        (hasUserClearedFilters.current ? '' : officeCookieValues.join(MULTI_SELECT_DELINEATOR)),
                      authContext.user?.member_id || null,
                      'office_name',
                      filterOptions?.offices,
                    ) as MultiselectAutocompleteOption[]
                  }
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedValuesList = value.map((valueItem) => valueItem.value);

                    // Reset the cleared flag when user makes a manual selection
                    hasUserClearedFilters.current = false;

                    table.getColumn('office_name')!.setFilterValue(selectedValuesList);
                    updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'office_name');
                    updateUrlSearchParams(searchParams, setSearchParams, {
                      office: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                    });
                  }}
                  sortFn={sortOfficeNames}
                  ref={officeFilterRef}
                  {...createTrackingProps({ elementName: 'Prior Auth Table: Office Filter', unmask: true })}
                />
                <TableMultiselectFilter
                  // Force remount when prescriber options load to ensure initialValue is applied correctly
                  key={filterOptions?.prescribers ? 'prescriber-loaded' : 'prescriber-loading'}
                  options={filterOptions?.prescribers || []}
                  label={'Prescriber'}
                  initialValue={
                    filterOptions?.prescribers
                      ? (getDropdownInitialValueFromSearchParams(
                          authContext.user?.default_filters,
                          searchParams.get('prescriber') || '',
                          'providers',
                          filterOptions.prescribers,
                        ) as MultiselectAutocompleteOption[])
                      : []
                  }
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedNamesList = value.map((valueItem) => valueItem.description);
                    const selectedNpisList = value.map((valueItem) => valueItem.value);

                    // Reset the cleared flag when user makes a manual selection
                    hasUserClearedFilters.current = false;

                    table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
                    updateUrlSearchParams(searchParams, setSearchParams, {
                      prescriber: selectedNpisList.length ? selectedNpisList.join(MULTI_SELECT_DELINEATOR) : undefined,
                    });
                  }}
                  ref={prescriberFilterRef}
                  {...createTrackingProps({ elementName: 'Prior Auth Table: Prescriber Filter', unmask: true })}
                />
                <TableMultiselectFilter
                  options={filterOptions?.drugs || []}
                  label={'Drug'}
                  initialValue={
                    getDropdownInitialValueFromSearchParamsAndCookie(
                      searchParams.get('drug_name') ||
                        (hasUserClearedFilters.current ? '' : drugCookieValues.join(MULTI_SELECT_DELINEATOR)),
                      authContext.user?.member_id || null,
                      'drug_name',
                      filterOptions?.drugs,
                    ) as MultiselectAutocompleteOption[]
                  }
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedValuesList = value.map((valueItem) => valueItem.value);
                    hasUserClearedFilters.current = false;
                    table.getColumn('drug')!.setFilterValue(selectedValuesList);
                    updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'drug_name');
                    updateUrlSearchParams(searchParams, setSearchParams, {
                      drug_name: selectedValuesList.length
                        ? selectedValuesList.join(MULTI_SELECT_DELINEATOR)
                        : undefined,
                    });
                  }}
                  ref={drugFilterRef}
                  {...createTrackingProps({ elementName: 'Prior Auth Table: Drug Filter', unmask: true })}
                />
                <SaveFiltersButton
                  $visible={showSaveButton}
                  onClick={() => setShowSaveModal(true)}
                  {...createTrackingProps({ elementName: 'Prior Auth Table: Save Filters Button' })}
                />

                <TableResultCount count={table.getRowModel().rows?.length ?? 0} />
              </FilterBarRow>
            )}
          </FilterBar>
          <Table role="tabpanel" id={panelId} aria-labelledby={tabListId}>
            <thead {...createTrackingProps({ unmask: true })}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHeader
                        id={header.id}
                        key={header.id}
                        style={{ width: '150px' }}
                        onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                        $sortable={header.column.getCanSort()}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        <HeaderSortingIndicator
                          isSorted={header.column.getIsSorted()}
                          sortDirection={header.column.getIsSorted()}
                        />
                      </TableHeader>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const RowComponent = row.original.pa.dismissed ? DismissedRow : ClickableRow;
                  return (
                    <RowComponent
                      key={row.id}
                      onClick={() => navigate(`/rxs/${row.original.rx.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          navigate(`/rxs/${row.original.rx.id}`);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </RowComponent>
                  );
                })
              ) : (
                <tr>
                  <TableCell colSpan={columns.length}>
                    <TableNullState
                      nullStateType={nullStateType}
                      onClearFilters={clearAllFilters}
                      onClearSearch={clearSearch}
                      onAction={() => navigate('/settings?tab=notifications')}
                      tableName="Expiring Prior Auth Table"
                    />
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </TableContainer>
      </OuterTableContainer>
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
    </>
  );
};

export default ExpiringPriorAuthTable;
